
// Custom JS

document.addEventListener("DOMContentLoaded", function(){

  // a11y: WAI-ARIA compliant keyboard navigation for the desktop header mega menu.
  //
  // The base Turbo theme (theme.js `Menu` class) opens dropdowns on `focusin`, which
  // creates a keyboard trap: Tab auto-expands "Shop" and the user must tab through every
  // link inside it to reach the next top-level item (WCAG 2.1.2 violation). It also has
  // no Esc-to-close and uses Tab (instead of Right Arrow) to enter flyout submenus.
  //
  // We cannot edit theme.js safely (Shopify overwrites it on theme updates), so this
  // controller intercepts the base focus handlers in the CAPTURE phase
  // (stopImmediatePropagation) and re-implements the full keyboard model in user space.
  // Dropdown visibility is class-driven only (`.nav-hover`, no `:focus-within` CSS), so
  // suppressing the focus handlers is enough to stop auto-open; we then drive open/close
  // ourselves. Pointer (hover/click) behavior is left entirely to the base theme.
  //
  // Golden rule: Tab moves between top-level items; Arrow keys navigate inside a menu.
  (function headerKeyboardNav() {
    const nav = document.getElementById('AccessibleNav');
    if (!nav || nav.dataset.kbdNavWired === 'true') return;
    nav.dataset.kbdNavWired = 'true';

    const overlayHeader = document.querySelector('header.overlay-header');

    // ---- structural helpers -------------------------------------------------
    const topItems = () => Array.from(nav.querySelectorAll(':scope > li.first-level-dropdown'));
    const triggerOf = (li) => li.querySelector(':scope > .site-nav__link');
    const dropdownOf = (li) => li.querySelector(':scope > .site-nav__dropdown');
    const hasMenu = (li) => !!dropdownOf(li);
    const topTriggers = () => topItems().map(triggerOf).filter(Boolean);
    const openTopItem = () => topItems().find((li) => li.classList.contains('nav-hover')) || null;

    const megaOf = (dd) => dd && dd.querySelector('.mega-menu-modern');
    const megaTabs = (mega) => Array.from(mega.querySelectorAll('.mega-modern-tab'));
    const activeMegaPanel = (mega) => mega.querySelector('.mega-modern-panel.active');
    const panelLinks = (panel) => Array.from(panel.querySelectorAll('a'));
    const selectedTab = (mega) => {
      const tabs = megaTabs(mega);
      return tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0] || null;
    };

    const isSpaceKey = (key) => key === ' ' || key === 'Spacebar';
    const isEscKey = (key) => key === 'Escape' || key === 'Esc';

    // A dropdown that was just revealed (visibility:hidden -> visible, with the theme's
    // `transition: all .5s` on the mega panel) is not focusable in the same task — the
    // browser needs a paint or two before `.focus()` takes. Focus synchronously (works
    // instantly for already-visible targets like tab/panel switches) and, only if it did
    // not stick, retry on the next frames. The `from` guard stops a stale retry from
    // yanking focus back once the user has navigated elsewhere.
    function focusWhenReady(el, { from = null, tries = 5 } = {}) {
      if (!el) return;
      el.focus();
      if (document.activeElement === el || tries <= 0) return;
      if (from && document.activeElement !== from) return;
      requestAnimationFrame(() => focusWhenReady(el, { from, tries: tries - 1 }));
    }

    // Move focus to the first focusable element AFTER the nav in DOM/Tab order
    // (e.g. the Search toggle). Used when Tab is pressed inside the LAST top-level
    // item's dropdown, where there is no next top-level trigger to advance to —
    // without this, focus loops back onto the same trigger and the user is trapped.
    function focusNextAfterNav() {
      const sel =
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const visible = (el) =>
        el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
      const next = Array.from(document.querySelectorAll(sel)).find(
        (el) =>
          !nav.contains(el) &&
          nav.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING &&
          visible(el)
      );
      next?.focus();
    }

    // aria-expanded belongs only on a trigger that actually owns a popup. Plain links
    // (e.g. "Contact") must never receive it, or screen readers announce them as
    // collapsed expandables.
    const setExpanded = (li, expanded) => {
      if (hasMenu(li)) triggerOf(li)?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    };

    // ---- open / close primitives -------------------------------------------
    function openTop(li, { focusFirst = false } = {}) {
      topItems().forEach((other) => {
        if (other !== li) closeTop(other);
      });
      li.classList.add('nav-hover');
      setExpanded(li, true);

      const dd = dropdownOf(li);
      if (overlayHeader) overlayHeader.classList.toggle('force-hover', !!megaOf(dd));

      if (!focusFirst || !dd) return;
      const from = triggerOf(li);
      const mega = megaOf(dd);
      if (mega) {
        focusWhenReady(selectedTab(mega), { from });
      } else {
        focusWhenReady(dd.querySelector('a.site-nav__link, a'), { from });
      }
    }

    function closeTop(li, { restoreFocus = false } = {}) {
      if (!li) return;
      li.classList.remove('nav-hover');
      setExpanded(li, false);
      // Collapse any open nested flyout submenus inside this top item.
      li.querySelectorAll('.site-nav--has-dropdown.nav-hover').forEach((n) => n.classList.remove('nav-hover'));
      if (overlayHeader) overlayHeader.classList.remove('force-hover');
      if (restoreFocus) triggerOf(li)?.focus();
    }

    const closeAllTops = () => topItems().forEach((li) => closeTop(li));

    // Mirror theme.js `Menu.activateTab` so tab/panel state stays consistent with the
    // theme's own (hover) activation path.
    function activateMegaTab(mega, tab, { focus = false } = {}) {
      if (!tab) return;
      const index = tab.dataset.tab;
      megaTabs(mega).forEach((t) => {
        const isActive = t === tab;
        t.classList.toggle('active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        t.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      mega.querySelectorAll('.mega-modern-panel').forEach((p) => {
        const isActive = p.dataset.panel === index;
        p.classList.toggle('active', isActive);
        if (isActive) p.removeAttribute('hidden');
        else p.setAttribute('hidden', '');
      });
      if (focus) tab.focus();
    }

    // ---- Step 0: suppress the base theme's focus-driven open/close ----------
    // Capture phase on the nav root runs before the base bubble-phase handlers on the
    // descendant <li>/<a> elements, so stopImmediatePropagation neutralizes them while
    // leaving pointer (mouseover/mouseenter/hoverintent) handlers untouched.
    nav.addEventListener(
      'focusin',
      (e) => {
        e.stopImmediatePropagation();
      },
      true
    );
    nav.addEventListener(
      'focusout',
      (e) => {
        const to = e.relatedTarget;
        // When keyboard focus leaves the nav entirely, close any open menu.
        if (!to || !nav.contains(to)) closeAllTops();
        e.stopImmediatePropagation();
      },
      true
    );

    // ---- delegated keyboard handling ---------------------------------------
    nav.addEventListener('keydown', onKeydown);

    function onKeydown(e) {
      const target = e.target;
      const key = e.key;

      // Esc: close the open menu and return focus to its top-level trigger.
      if (isEscKey(key)) {
        const openLi = openTopItem();
        if (openLi) {
          e.preventDefault();
          closeTop(openLi, { restoreFocus: true });
        }
        return;
      }

      // Step 4 — Tab inside an open menu: close and skip to the adjacent top item,
      // so the user is never trapped inside a long dropdown.
      if (key === 'Tab') {
        const openLi = openTopItem();
        const dd = openLi && dropdownOf(openLi);
        if (openLi && dd && dd.contains(target)) {
          const triggers = topTriggers();
          const idx = triggers.indexOf(triggerOf(openLi));
          // Shift+Tab returns to the menu's own trigger; Tab advances to the next
          // top-level trigger. On the last top item there is no next trigger, so
          // exit the nav to the first focusable element after it (e.g. Search)
          // instead of looping back onto the same trigger and trapping focus.
          const dest = e.shiftKey ? triggers[idx] : triggers[idx + 1];
          e.preventDefault();
          closeTop(openLi);
          if (dest) dest.focus();
          else focusNextAfterNav();
        }
        return;
      }

      // Top-level trigger (Shop / GUA / About …).
      const trigEl = target.closest('.site-nav__link');
      const topLi = trigEl ? topItems().find((li) => triggerOf(li) === trigEl) : null;
      if (topLi) return onTopTriggerKeydown(e, topLi);

      // Inside the modern mega menu.
      if (target.classList.contains('mega-modern-tab')) return onMegaTabKeydown(e, target);
      const panel = target.closest('.mega-modern-panel');
      if (panel && nav.contains(panel)) return onMegaPanelKeydown(e, target, panel);

      // Inside a standard (non-mega) dropdown.
      const std = target.closest('.site-nav__dropdown.standard-dropdown');
      if (std) return onStandardKeydown(e, target, std);
    }

    // Step 1 — open on Space / ArrowDown (and Enter only for the hrefless "Shop" button);
    // Enter on a real link is left to navigate to its category landing page.
    function onTopTriggerKeydown(e, li) {
      if (!hasMenu(li)) return;
      const trigger = triggerOf(li);
      const isButtonLike = trigger.tagName === 'SPAN' || trigger.getAttribute('role') === 'button';
      const shouldOpen = e.key === 'ArrowDown' || isSpaceKey(e.key) || (e.key === 'Enter' && isButtonLike);
      if (shouldOpen) {
        e.preventDefault();
        openTop(li, { focusFirst: true });
      }
    }

    // Step 3 — modern mega tabs: Up/Down switch tabs, Right/Enter enter the panel.
    function onMegaTabKeydown(e, tab) {
      const mega = tab.closest('.mega-menu-modern');
      if (!mega) return;
      const tabs = megaTabs(mega);
      const idx = tabs.indexOf(tab);
      const last = tabs.length - 1;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          activateMegaTab(mega, tabs[idx === last ? 0 : idx + 1], { focus: true });
          break;
        case 'ArrowUp':
          e.preventDefault();
          activateMegaTab(mega, tabs[idx === 0 ? last : idx - 1], { focus: true });
          break;
        case 'Home':
          e.preventDefault();
          activateMegaTab(mega, tabs[0], { focus: true });
          break;
        case 'End':
          e.preventDefault();
          activateMegaTab(mega, tabs[last], { focus: true });
          break;
        case 'ArrowRight':
        case 'Enter': {
          const panel = activeMegaPanel(mega);
          const links = panel ? panelLinks(panel) : [];
          if (links.length) {
            e.preventDefault();
            links[0].focus();
          }
          break;
        }
      }
    }

    // Step 3 — modern mega panel links: Up/Down browse, Left returns to the parent tab.
    function onMegaPanelKeydown(e, link, panel) {
      const links = panelLinks(panel);
      const idx = links.indexOf(link);
      if (idx === -1) return;
      const last = links.length - 1;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          links[idx === last ? 0 : idx + 1]?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          links[idx === 0 ? last : idx - 1]?.focus();
          break;
        case 'Home':
          e.preventDefault();
          links[0]?.focus();
          break;
        case 'End':
          e.preventDefault();
          links[last]?.focus();
          break;
        case 'ArrowLeft': {
          e.preventDefault();
          const mega = panel.closest('.mega-menu-modern');
          selectedTab(mega)?.focus();
          break;
        }
      }
    }

    // Step 5 — standard dropdowns: Up/Down through the list, Right opens a flyout
    // submenu, Left returns from a flyout to its parent link.
    function onStandardKeydown(e, target, std) {
      const sub = target.closest('.site-nav__subdropdown');
      if (sub) {
        const links = Array.from(sub.querySelectorAll('a'));
        const idx = links.indexOf(target);
        const last = links.length - 1;
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            links[idx === last ? 0 : idx + 1]?.focus();
            break;
          case 'ArrowUp':
            e.preventDefault();
            links[idx === 0 ? last : idx - 1]?.focus();
            break;
          case 'Home':
            e.preventDefault();
            links[0]?.focus();
            break;
          case 'End':
            e.preventDefault();
            links[last]?.focus();
            break;
          case 'ArrowLeft': {
            e.preventDefault();
            const parentLi = sub.closest('li.site-nav--has-dropdown');
            parentLi?.classList.remove('nav-hover');
            parentLi?.querySelector(':scope > a.site-nav__link')?.focus();
            break;
          }
        }
        return;
      }

      const items = Array.from(std.querySelectorAll(':scope > li > a.site-nav__link'));
      const idx = items.indexOf(target);
      if (idx === -1) return;
      const last = items.length - 1;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          items[idx === last ? 0 : idx + 1]?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          items[idx === 0 ? last : idx - 1]?.focus();
          break;
        case 'Home':
          e.preventDefault();
          items[0]?.focus();
          break;
        case 'End':
          e.preventDefault();
          items[last]?.focus();
          break;
        case 'ArrowRight': {
          const li = target.closest('li.site-nav--has-dropdown');
          const flyout = li && li.querySelector(':scope > .site-nav__subdropdown');
          if (flyout) {
            e.preventDefault();
            li.classList.add('nav-hover');
            focusWhenReady(flyout.querySelector('a'), { from: target });
          }
          break;
        }
      }
    }

    // ---- pointer parity: keep hover/click tab activation (was per-tab before) ----
    nav.querySelectorAll('.mega-menu-modern').forEach((mega) => {
      megaTabs(mega).forEach((tab) => {
        tab.addEventListener('mouseenter', () => activateMegaTab(mega, tab));
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          activateMegaTab(mega, tab, { focus: true });
        });
      });
    });

    // ---- initialize ARIA state on top-level triggers that own a menu ----
    topItems().forEach((li) => setExpanded(li, false));
  })();
  

   // Megamenu dropdown position  

  (function () {

    function positionAll() {
    const items = document.querySelectorAll('.site-nav__dropdown.mega-menu-dropdown');
    

    items.forEach(item => {
      const parent = item.closest('.site-nav--has-dropdown');
      if (!parent) return;

      item.style.setProperty('left', '0px', 'important');
      item.style.setProperty('right', 'auto', 'important');
    
      let wasHidden = false;
      const cs = window.getComputedStyle(item);
    
      if (cs.display === 'none') {
        wasHidden = true;
        item.style.display = 'block';
        item.style.visibility = 'hidden';
      }
    
      const parentRect = parent.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
    
      let dropdownWidth = item.getBoundingClientRect().width;
      dropdownWidth = Math.min(dropdownWidth, 1200);

      let left = parentRect.left + parentRect.width / 2 - dropdownWidth / 2;

      if (left + dropdownWidth > viewportWidth) {
        left = viewportWidth - dropdownWidth;
      }

      if (left < 0) {
        left = 0;
      }

      const shift = left - parentRect.left;
    
      item.style.setProperty(
        'transform',
        `translateX(${shift}px)`,
        'important'
      );
    
      if (wasHidden) {
        item.style.display = '';
        item.style.visibility = '';
      }
    });
    
    }
    
    document.addEventListener('DOMContentLoaded', positionAll);
    window.addEventListener('load', positionAll);
    
    let t;
    window.addEventListener('resize', () => {
    clearTimeout(t);
    t = setTimeout(positionAll, 150);
    });
    
    })();

    // PDP Custom Blocks Organization
    

    const organizePDPCustomBlocks = () => {

      if (!document.body.classList.contains('template-product')) return;

      const guaForm = document.querySelector('.product-form-block[id*="returns_return_product_page"]');
      if (guaForm) {
        return organizeGUACustomBlocks();
      }

      const qtyBlock = document.querySelector('.product-form-block[data-block-id="quantity"]');
      if (!qtyBlock) return;

      const container = document.createElement('div');
      container.classList.add('product-form-block', 'app-buttons-block');
      qtyBlock.after(container);

      const cardSelectors = ['.emb-card', '.mdta-card', '.mleash-card', '.snap-card'];

      let equalizeTimer;
      const scheduleEqualize = () => {
        clearTimeout(equalizeTimer);
        equalizeTimer = setTimeout(() => {
          const cards = cardSelectors.flatMap((sel) => [...container.querySelectorAll(sel)]);
          if (!cards.length) return;
          cards.forEach((c) => (c.style.minHeight = ''));
          const maxHeight = Math.max(...cards.map((c) => c.getBoundingClientRect().height));
          cards.forEach((c) => (c.style.minHeight = maxHeight + 'px'));
        }, 300);
      };

      const selectors = [
        '.product-form-block[id*="embroidery_configurator"]',
        '.product-form-block[id*="dta_boomerang_matching_dta"]',
        '.product-form-block[id*="leash_add_on_matching_leash"]',
        '.product-form-block[id*="snap_option_gate_snap"]',
      ];

      const insertInOrder = (el) => {
        const elIndex = selectors.findIndex((s) => el.matches(s));
        const children = [...container.children];
        const after = children.findLast((c) => selectors.findIndex((s) => c.matches(s)) < elIndex);
        if (after) after.after(el);
        else container.prepend(el);
      };

      selectors.forEach((selector) => {
        const el = document.querySelector(selector);
        if (!el) return;

        const observer = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.contentRect.height > 0) {
              insertInOrder(el);
              observer.unobserve(el);
              scheduleEqualize();
            }
          }
        });
        observer.observe(el);
      });
    };

    const organizeGUACustomBlocks = () => {
      const guaNextBtn = document.querySelector('.product-form-block[id*="returns_return_next_button"]');
      const container = document.createElement('div');
      container.classList.add('product-form-block', 'app-buttons-block');
      guaNextBtn.before(container);

      const cardSelectors = ['.emb-card', '.mdta-card', '.mleash-card', '.gsnap-card'];

      let equalizeTimer;
      const scheduleEqualize = () => {
        clearTimeout(equalizeTimer);
        equalizeTimer = setTimeout(() => {
          const cards = cardSelectors.flatMap((sel) => [...container.querySelectorAll(sel)]);
          if (!cards.length) return;
          cards.forEach((c) => (c.style.minHeight = ''));
          const maxHeight = Math.max(...cards.map((c) => c.getBoundingClientRect().height));

          console.log(maxHeight);
          cards.forEach((c) => (c.style.minHeight = maxHeight + 'px'));
        }, 300);
      };

      const selectors = [
        '.product-form-block[id*="embroidery_configurator"]',        
        '.product-form-block[id*="snap_option_gate_snap"]',
      ];

      selectors.forEach((selector) => {
        const el = document.querySelector(selector);
        if (!el) return;

        const observer = new ResizeObserver((entries) => {
          for (const entry of entries) {            
              container.appendChild(el);
              observer.unobserve(el);
              scheduleEqualize();            
          }
        });
        observer.observe(el);
      });
    }

    organizePDPCustomBlocks();

    // Open live chat from links ending in #open-chat; falls back to href if psOpenWindow is unavailable.
    document.addEventListener('click', function (e) {
      const link = e.target.closest('a[href$="#open-chat"]');
      if (!link) return;
      if (typeof window.psOpenWindow === 'function') {
        e.preventDefault();
        window.psOpenWindow('a3Qv');
      }
    });

    // a11y: keep mobile NavDrawer focusable whenever the mobile nav bar is shown.
    // Theme core disables tabindex above 767px; the mobile breakpoint is actually 989px,
    // so at 150% zoom (or any narrow desktop) the open Shop submenu becomes unreachable
    // by keyboard, which screen-reader users perceive as a focus trap on "Shop".
    (function () {
      const MOBILE_BP = 989;
      const drawer = document.getElementById('NavDrawer');
      if (!drawer) return;

      const syncTabindex = () => {
        const isMobileView = window.innerWidth <= MOBILE_BP;
        drawer.querySelectorAll('a, button').forEach((el) => {
          if (el.dataset.preserveTabindex === 'true') return;
          if (isMobileView) {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', '-1');
          }
        });
      };

      syncTabindex();
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(syncTabindex, 100);
      });

      // Keep aria-expanded in sync with the actual open/closed state of each submenu,
      // and let keyboard users activate the submenu trigger with Enter/Space (the
      // theme only wires click on the chevron button, which has tabindex=-1).
      const triggers = drawer.querySelectorAll('.mobile-nav__link[aria-controls^="mobile-submenu-"]');
      triggers.forEach((trigger) => {
        const hasSublist = trigger.closest('.mobile-nav__has-sublist');
        if (!hasSublist) return;

        const syncExpanded = () => {
          trigger.setAttribute('aria-expanded', hasSublist.classList.contains('mobile-nav--expanded') ? 'true' : 'false');
        };

        // Observe class changes triggered by the theme's existing chevron click handler
        new MutationObserver(syncExpanded).observe(hasSublist, { attributes: true, attributeFilter: ['class'] });
        syncExpanded();

        // Enter / Space activates the trigger. For <a href>, only handle Space — Enter
        // should still follow the link as expected. For the Shop <span role="button">,
        // both keys must activate (it has no href).
        trigger.addEventListener('keydown', (e) => {
          const isSpace = e.key === ' ' || e.key === 'Spacebar';
          const isEnter = e.key === 'Enter';
          if (!isSpace && !(isEnter && trigger.tagName === 'SPAN')) return;
          e.preventDefault();
          hasSublist.querySelector('.mobile-nav__toggle')?.click();
        });
      });
    })();

    // a11y: client-side validation feedback for newsletter forms (footer + popup).
    // Browsers natively block submission on invalid <input type="email" required>, but
    // they do not move focus consistently nor expose the error to screen readers via
    // the field's aria-describedby. We listen for the form's `invalid` event (which
    // fires per-field before submit when constraint validation fails) and:
    //   - set aria-invalid="true" on the field
    //   - inject the browser's validation message into the linked error container
    //   - move focus to the first invalid field
    (function () {
      const forms = [
        { formId: 'newsletter-footer_form',  errorsId: 'newsletter-footer-errors' },
        { formId: 'newsletter_popup_form',   errorsId: null }, // popup has two forms with same id (fullscreen+slideup), handled below
      ];

      const wireForm = (form, errorsContainer) => {
        if (!form || !errorsContainer) return;
        const inputs = form.querySelectorAll('input[type="email"], input[required], input[pattern]');
        let firstInvalid = null;
        let focusScheduled = false;

        // Listen on the form itself so we can clear server-rendered errors before each
        // submission attempt, but BEFORE the browser's constraint check runs.
        // When constraint validation fails, the browser fires `invalid` per-field and
        // skips the `submit` event entirely — so we cannot rely on submit firing.
        const handleAttempt = () => {
          firstInvalid = null;
          focusScheduled = false;
          errorsContainer.querySelectorAll('.js-client-error').forEach((n) => n.remove());
          inputs.forEach((i) => i.removeAttribute('aria-invalid'));
          // If no server-rendered errors remain inside, drop the .errors box styling so
          // the container does not render as an empty red stripe.
          if (!errorsContainer.querySelector('.error, .errors__list, li, p, span')) {
            errorsContainer.classList.remove('errors');
          }
        };

        // The button's click is the earliest reliable signal of "the user is trying to submit".
        // We use both the click (covers requestSubmit() too via button activation in real UX)
        // and 'formdata' (covers programmatic submits) to clear stale state.
        form.addEventListener('click', (e) => {
          if (e.target.closest('button[type="submit"], button:not([type])')) handleAttempt();
        }, true);
        form.addEventListener('formdata', handleAttempt);

        inputs.forEach((input) => {
          input.addEventListener('invalid', (e) => {
            e.preventDefault(); // suppress the native bubble; we present the error ourselves
            input.setAttribute('aria-invalid', 'true');
            const msg = document.createElement('span');
            msg.className = 'error js-client-error';
            msg.textContent = input.validationMessage || 'Please provide a valid value.';
            // Apply the .errors box styling now that the container actually has content.
            errorsContainer.classList.add('errors');
            errorsContainer.appendChild(msg);
            if (!firstInvalid) firstInvalid = input;

            // Schedule a single focus shift to the first invalid field after the current
            // burst of `invalid` events has settled.
            if (!focusScheduled) {
              focusScheduled = true;
              setTimeout(() => {
                firstInvalid?.focus();
                focusScheduled = false;
              }, 0);
            }
          });

          input.addEventListener('input', () => {
            if (input.validity.valid) {
              input.removeAttribute('aria-invalid');
            }
          });
        });
      };

      // Footer form
      wireForm(
        document.getElementById('newsletter-footer_form'),
        document.getElementById('newsletter-footer-errors')
      );

      // Both popup forms share id="newsletter_popup_form" — disambiguate by their errors container
      document.querySelectorAll('#newsletter_popup_form').forEach((form) => {
        const errorsId = form.closest('#fullscreen')
          ? 'newsletter-popup-fs-errors'
          : 'newsletter-popup-su-errors';
        wireForm(form, document.getElementById(errorsId));
      });

      // Server-rendered errors: when the form is submitted as a real POST to /contact the
      // page does a full reload, so the error markup is already present in the DOM at load
      // time. role="alert"/aria-live only announce content that changes AFTER the screen
      // reader has built the a11y tree — they stay silent on errors baked into the initial
      // HTML. So the footer/popup errors went unread (QA: "VoiceOver does not read this
      // error after reload"). Fix: if a container has server-rendered error content on load,
      // move focus to it so the screen reader announces it. We focus the container (not the
      // field) because the error text lives there and the field only references it via
      // aria-describedby, which is not announced on programmatic focus in all SR/browser combos.
      const focusServerError = (errorsContainer) => {
        if (!errorsContainer) return false;
        // Ignore JS-injected client errors — those flows already manage focus themselves.
        const hasServerError = errorsContainer.querySelector(
          '.error:not(.js-client-error), .errors__list, li, p'
        );
        if (!hasServerError) return false;
        errorsContainer.setAttribute('tabindex', '-1');
        errorsContainer.focus();
        errorsContainer.scrollIntoView({ block: 'center' });
        return true;
      };

      // The footer error region is always in the DOM; the popup may need to be opened first,
      // but if it rendered an error inline it is already present. Focus the first one found.
      const allErrorRegions = [
        document.getElementById('newsletter-footer-errors'),
        document.getElementById('newsletter-popup-fs-errors'),
        document.getElementById('newsletter-popup-su-errors'),
      ];
      allErrorRegions.some((region) => focusServerError(region));
    })();

    // a11y: Slideshow autoplay pause + live-region announcements (WCAG 2.2.2, 4.1.3).
    // The theme initializes Swiper with `autoplay.enabled: true` and no built-in
    // pause control. We dynamically inject a pause button and a polite live
    // region next to each slideshow, then hook into the Swiper instance.
    (function () {
      const initSlideshow = (sw) => {
        const swiper = sw.swiper;
        if (!swiper || sw.dataset.a11yWired === 'true') return;
        sw.dataset.a11yWired = 'true';

        // Wrap the swiper so we can absolutely-position the button
        sw.style.position = sw.style.position || 'relative';

        // Pause button
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'slideshow__pause';
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Pause slideshow');
        btn.innerHTML = '<span aria-hidden="true">&#10073;&#10073;</span><span class="visually-hidden">Pause slideshow</span>';
        sw.appendChild(btn);

        // Polite live region for slide changes
        const live = document.createElement('div');
        live.className = 'visually-hidden';
        live.setAttribute('aria-live', 'polite');
        live.setAttribute('aria-atomic', 'true');
        sw.appendChild(live);

        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReduced && swiper.autoplay?.stop) {
          swiper.autoplay.stop();
          btn.setAttribute('aria-pressed', 'true');
        }

        btn.addEventListener('click', () => {
          const isPaused = btn.getAttribute('aria-pressed') === 'true';
          if (isPaused) {
            swiper.autoplay?.start?.();
            btn.setAttribute('aria-pressed', 'false');
          } else {
            swiper.autoplay?.stop?.();
            btn.setAttribute('aria-pressed', 'true');
          }
        });

        const announceSlide = () => {
          try {
            const slides = sw.querySelectorAll('.swiper-slide');
            const total = slides.length;
            // Use the visually-active slide for index (Swiper marks it with .swiper-slide-active)
            let idx = 0;
            slides.forEach((s, i) => { if (s.classList.contains('swiper-slide-active')) idx = i; });
            const active = slides[idx];
            const title = active?.querySelector('.slideshow-title, h1, h2, h3')?.textContent?.trim()
              || active?.getAttribute('aria-label')
              || '';
            live.textContent = `Slide ${idx + 1} of ${total}${title ? ': ' + title : ''}`;
          } catch (e) { /* no-op */ }
        };

        // Observe class changes on the slides to catch every advance,
        // regardless of how it was triggered (autoplay, click, swipe, dot).
        // Attach to the swiper root with subtree:true so we don't hold a stale
        // reference to a wrapper Swiper may rebuild after init.
        new MutationObserver(announceSlide).observe(sw, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true,
        });
      };

      const tryInit = () => {
        document.querySelectorAll('[data-wetheme-section-type="image--slideshow"] .swiper').forEach((sw) => {
          if (sw.swiper) initSlideshow(sw);
        });
      };

      // Swiper is initialized after DOMContentLoaded; poll briefly until it's there
      tryInit();
      let attempts = 0;
      const id = setInterval(() => {
        tryInit();
        if (++attempts > 20) clearInterval(id);
      }, 250);
    })();

    // a11y: Pause/resume the announcement-bar rotation (WCAG 2.2.2 Pause/Stop/Hide).
    // The theme rotates announcement blocks every 7 seconds with no UI to stop it.
    // The Liquid snippet renders a <button data-announcement-pause> when more than
    // one block is present; here we toggle the underlying setInterval.
    (function () {
      const bar = document.querySelector('animated-background-text-colors');
      const btn = document.querySelector('[data-announcement-pause]');
      if (!bar || !btn) return;

      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced && bar.interval) {
        // Respect OS-level setting and stop the rotation by default.
        clearInterval(bar.interval);
        bar.interval = null;
        btn.setAttribute('aria-pressed', 'true');
        btn.querySelectorAll('.visually-hidden, [aria-label]').forEach((el) => {
          // no label swap; aria-pressed conveys the toggle state
        });
      }

      btn.addEventListener('click', () => {
        const isPaused = btn.getAttribute('aria-pressed') === 'true';
        if (isPaused) {
          // Resume — recreate the interval using the element's own changeColors method
          if (typeof bar.changeColors === 'function') {
            bar.interval = setInterval(bar.changeColors.bind(bar), 7000);
          }
          btn.setAttribute('aria-pressed', 'false');
        } else {
          if (bar.interval) {
            clearInterval(bar.interval);
            bar.interval = null;
          }
          btn.setAttribute('aria-pressed', 'true');
        }
      });
    })();

    // a11y: Pause/resume the scrolling-banner ticker (WCAG 2.2.2).
    // The theme uses CSS animation that only pauses on :hover. Toggle a class
    // on the section root so the animation honors the user's choice instead.
    document.querySelectorAll('.scrolling-banner-section').forEach((section) => {
      const btn = section.querySelector('[data-scrolling-banner-pause]');
      if (!btn) return;
      const banner = section.querySelector('scrolling-banner');
      if (!banner) return;

      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) {
        section.classList.add('scrolling-banner--paused');
        btn.setAttribute('aria-pressed', 'true');
      }

      btn.addEventListener('click', () => {
        const isPaused = section.classList.toggle('scrolling-banner--paused');
        btn.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
      });
    });

    // a11y: Close collection "Filter By" disclosure when keyboard focus leaves it.
    // Native <details> stays open on blur. WebAIM flagged that the open dropdown
    // obscures the page (including elements receiving focus). When focus moves
    // outside the <details>, collapse it.
    document.querySelectorAll('details.facets__disclosure').forEach((details) => {
      details.addEventListener('focusout', (e) => {
        // Only auto-close on genuine keyboard navigation OUT of the disclosure —
        // i.e. focus moving to another real element (relatedTarget is non-null).
        // Selecting a filter with the mouse, or the AJAX facet re-render replacing
        // the focused checkbox, drops focus to <body> with a null relatedTarget;
        // that must NOT collapse the panel, otherwise every filter click closes the
        // "Filter By" dropdown (QA: filters appear unusable on collection pages).
        if (!e.relatedTarget) return;
        // Defer one tick so document.activeElement reflects the new focus
        setTimeout(() => {
          if (!details.open) return;
          if (details.contains(document.activeElement)) return;
          details.removeAttribute('open');
        }, 0);
      });
    });

    // a11y: Cart drawer — focus restore on close + Esc to close (WCAG 2.4.3, 2.1.1).
    // The drawer is opened two ways: (a) user clicks a [aria-controls="CartDrawer"]
    // element in the header, or (b) programmatically via the eventBus
    // "open:cart:drawer" event (e.g. after Add-to-Cart in component-product-form.js).
    // In case (b) no cart-icon click is captured, so the original triggerToRestore
    // is null and focus was never restored — broken for both Esc and Close-button.
    // The Esc path had an additional timing gap: the global keyup handler in
    // theme.js (onKeyUp, keyCode 27) emits close:all:drawers after the keydown
    // already closed the drawer, so a synchronous focus() could be disrupted.
    //
    // Fix: fall back to the visible [aria-controls="CartDrawer"] toggle when no
    // explicit opener was captured; defer the focus() call via requestAnimationFrame
    // so it runs AFTER (1) theme.js's keyup→close:all:drawers handler and (2) any
    // third-party document-level Escape handler that synchronously steals focus on
    // keydown — notably the classic Judge.me widget (judgeme_core) on PDPs, which
    // moves focus to its "Write a review" button. The cart drawer is a focus-trapped
    // modal, so on close focus was always inside it — there is no deliberate user
    // move to preserve, so we re-assert focus to the opener unconditionally.
    //
    // The <cart-drawer> custom element reflects open state as aria-hidden="false"/
    // "true" — observe that attribute, not a documentElement class.
    (function () {
      const drawer = document.getElementById('CartDrawer') || document.querySelector('cart-drawer');
      if (!drawer) return;

      let triggerToRestore = null;

      // Capture the opener when the user clicks the cart icon (or any
      // [aria-controls="CartDrawer"] element) so we can return focus to it.
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[aria-controls="CartDrawer"]');
        if (opener) triggerToRestore = opener;
      }, true);

      const isOpen = () => drawer.getAttribute('aria-hidden') === 'false';

      // There are two cart toggles (desktop + mobile); find the visible one as
      // a fallback when the drawer was opened without a triggering click.
      const getCartToggle = () =>
        Array.from(document.querySelectorAll('[aria-controls="CartDrawer"]')).find(
          (el) => el.offsetParent !== null,
        );

      const closeDrawer = () => {
        drawer.querySelector('[data-drawer-close]:not(.global-drawer__overlay)')?.click();
      };

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' && e.key !== 'Esc') return;
        if (!isOpen()) return;
        e.preventDefault();
        closeDrawer();
      });

      // Watch the drawer's own aria-hidden to detect the open→closed transition,
      // then return focus to the opener. If no opener was captured (programmatic
      // open), fall back to the visible header cart toggle. requestAnimationFrame
      // ensures the focus call runs after the keyup Esc handler in theme.js
      // (close:all:drawers) completes, fixing the Esc-specific focus loss.
      let wasOpen = isOpen();
      new MutationObserver(() => {
        const open = isOpen();
        if (wasOpen && !open) {
          const target = triggerToRestore && document.contains(triggerToRestore)
            ? triggerToRestore
            : getCartToggle();
          triggerToRestore = null;
          if (target) {
            requestAnimationFrame(() => {
              // Re-assert unless focus already landed on the opener. The rAF runs
              // after third-party Escape handlers (e.g. Judge.me) have synchronously
              // moved focus, so this reliably wins it back to the cart toggle.
              if (document.activeElement !== target) target.focus();
            });
          }
        }
        wasOpen = open;
      }).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    })();

    // a11y: "Lead the Pack" popup — make it behave like a proper modal dialog.
    // The theme's Popup class shows/hides the popup but doesn't implement focus trap,
    // Esc-to-close, or focus restoration. Augment it here.
    (function () {
      const popupRoot = document.getElementById('popup');
      if (!popupRoot) return;

      const fullscreenEl = document.getElementById('fullscreen');
      const slideUpEl = document.getElementById('slide-up');

      // The active dialog container at any moment
      const getActiveDialog = () => {
        if (fullscreenEl && getComputedStyle(fullscreenEl).display !== 'none') {
          return fullscreenEl.closest('.fullscreen-popup') || fullscreenEl;
        }
        if (slideUpEl && getComputedStyle(slideUpEl).visibility !== 'hidden') {
          return slideUpEl;
        }
        return null;
      };

      // Element that had focus before the popup opened — restore on close
      let triggerToRestore = null;

      const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const getFocusables = (root) => Array.from(root.querySelectorAll(focusableSelector)).filter((el) => {
        const cs = getComputedStyle(el);
        return cs.visibility !== 'hidden' && cs.display !== 'none';
      });

      const closePopup = () => {
        const closeBtn = popupRoot.querySelector('.popup--close, .popup--close-btn');
        closeBtn?.click();
      };

      const onKeydown = (e) => {
        const dialog = getActiveDialog();
        if (!dialog) return;

        if (e.key === 'Escape' || e.key === 'Esc') {
          e.preventDefault();
          closePopup();
          return;
        }

        if (e.key !== 'Tab') return;

        const focusables = getFocusables(dialog);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (!dialog.contains(document.activeElement)) {
          // Focus escaped (e.g. from somewhere on the page) — pull it back in
          e.preventDefault();
          first.focus();
        }
      };

      // Detect popup open via class change ("visible" added)
      const observer = new MutationObserver(() => {
        const isVisible = popupRoot.classList.contains('visible');
        if (isVisible) {
          triggerToRestore = document.activeElement;
          document.addEventListener('keydown', onKeydown);
        } else {
          document.removeEventListener('keydown', onKeydown);
          if (triggerToRestore && document.contains(triggerToRestore)) {
            triggerToRestore.focus();
          }
          triggerToRestore = null;
        }
      });
      observer.observe(popupRoot, { attributes: true, attributeFilter: ['class'] });
    })();

  // Search results page: mirror the header's "Add wildcard (*) to search query" setting.
  //
  // `settings.predictive_search_wildcard` was only wired into the header dropdown — the
  // <predictive-search> element appends `*` itself in handleSubmit
  // (component-predictive-search.js). The form on the search results page
  // (snippets/search-bar.liquid) is a plain native GET form with no JS, so a second search
  // made from that page silently dropped the wildcard and fell back to exact-term matching.
  // theme.js (`SearchResults.removeAsterisks`) also strips the `*` out of the input for
  // display, so by the time the shopper resubmits the value is always the bare term.
  //
  // Liquid prints the setting into `data-search-wildcard`, matching how the header passes it
  // to <predictive-search>. The listener is delegated on document so it survives the search
  // section being re-rendered (filtering / sorting).
  (function searchPageWildcard() {
    document.addEventListener('submit', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const form = target.closest('form.search-bar[data-search-wildcard="true"]');
      if (!form) return;

      const input = form.querySelector('input[name="q"]');
      if (!input) return;

      const term = input.value.trim();
      if (term === '' || term.endsWith('*')) return;

      // Carry the wildcard term in a hidden twin rather than rewriting the visible input:
      // rewriting it makes "cat" visibly flash to "cat*" in the field during the moment
      // between submit and navigation. Dropping the visible input's `name` takes it out of
      // the form data set, so only the hidden field's value is sent. Native submission is
      // left intact, so any other fields on the form still travel with it.
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'q';
      hidden.value = `${term}*`;
      input.removeAttribute('name');
      form.appendChild(hidden);

      // The form data set is built synchronously once this handler returns, so undoing the
      // swap in a later task is safe — and it keeps the form working if the navigation never
      // happens (blocked unload, restore from bfcache).
      setTimeout(() => {
        input.setAttribute('name', 'q');
        hidden.remove();
      }, 0);
    });
  })();

});